// apps/web/src/pages/planejamento/kanban/kanban-filtros.tsx — Fase 11, Plano 17 (KAN-02, D-33).
// Barra de filtros do quadro (densidade compacta): cliente, prioridade, subtipo e célula.
import { ChevronDown, FilterX } from 'lucide-react';
import type { NivelPrioridadeKanban } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  filtrosAtivos,
  PRIORIDADES_KANBAN,
  SUBTIPOS_BANHO,
  SUBTIPOS_TINTA,
  type FiltrosKanban,
  type OpcoesFiltroKanban,
} from '../../../lib/kanban/filtros';

const TODOS_CLIENTES = '__todos__';

const ROTULO_PRIORIDADE: Record<NivelPrioridadeKanban, string> = {
  urgente: 'Urgente',
  alta: 'Alta',
  normal: 'Normal',
};

interface KanbanFiltrosProps {
  filtros: FiltrosKanban;
  opcoes: OpcoesFiltroKanban;
  onMudar: (filtros: FiltrosKanban) => void;
}

function alternar<T>(lista: readonly T[], valor: T): T[] {
  return lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor];
}

function Chip({ ativo, onClick, children }: { ativo: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={onClick}
      className={`h-8 rounded-full border px-3 text-xs font-medium transition-colors ${
        ativo
          ? 'border-blue-700 bg-blue-700 text-white'
          : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300'
      }`}
    >
      {children}
    </button>
  );
}

export function KanbanFiltros({ filtros, opcoes, onMudar }: KanbanFiltrosProps) {
  const clienteAtual = filtros.cliente ?? TODOS_CLIENTES;
  // Se o cliente salvo não está mais no quadro, ele continua listado para poder ser trocado.
  const clientes =
    filtros.cliente !== null && !opcoes.clientes.includes(filtros.cliente)
      ? [filtros.cliente, ...opcoes.clientes]
      : opcoes.clientes;

  return (
    <div
      data-density="compact"
      className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-200 bg-white px-6 py-2"
      role="group"
      aria-label="Filtros do quadro"
    >
      <Select
        value={clienteAtual}
        onValueChange={(valor) => onMudar({ ...filtros, cliente: valor === TODOS_CLIENTES ? null : valor })}
      >
        <SelectTrigger size="sm" className="w-52" aria-label="Filtrar por cliente">
          <SelectValue placeholder="Cliente" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={TODOS_CLIENTES}>Todos os clientes</SelectItem>
          {clientes.map((cliente) => (
            <SelectItem key={cliente} value={cliente}>
              {cliente}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center gap-1.5" role="group" aria-label="Prioridade">
        {PRIORIDADES_KANBAN.map((nivel) => (
          <Chip
            key={nivel}
            ativo={filtros.prioridades.includes(nivel)}
            onClick={() => onMudar({ ...filtros, prioridades: alternar(filtros.prioridades, nivel) })}
          >
            {ROTULO_PRIORIDADE[nivel]}
          </Chip>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Subtipo">
        {[...SUBTIPOS_BANHO, ...SUBTIPOS_TINTA].map((subtipo) => (
          <Chip
            key={subtipo}
            ativo={filtros.subtipos.includes(subtipo)}
            onClick={() => onMudar({ ...filtros, subtipos: alternar(filtros.subtipos, subtipo) })}
          >
            {subtipo}
          </Chip>
        ))}
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-2 text-slate-700" aria-label="Filtrar por célula">
            {filtros.celulas.length === 0 ? 'Células' : `Células (${filtros.celulas.length})`}
            <ChevronDown className="h-4 w-4 opacity-50" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-2">
          {opcoes.celulas.length === 0 ? (
            <p className="p-2 text-sm text-slate-500">Nenhuma célula no quadro.</p>
          ) : (
            <ul className="flex max-h-64 flex-col overflow-y-auto">
              {opcoes.celulas.map((celula) => (
                <li key={celula}>
                  <label className="flex min-h-8 cursor-pointer items-center gap-2 rounded px-2 text-sm hover:bg-slate-100">
                    <Checkbox
                      checked={filtros.celulas.includes(celula)}
                      onCheckedChange={() => onMudar({ ...filtros, celulas: alternar(filtros.celulas, celula) })}
                    />
                    {celula}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </PopoverContent>
      </Popover>

      <Button
        variant="ghost"
        size="sm"
        className="h-8 gap-1.5 text-slate-600"
        disabled={!filtrosAtivos(filtros)}
        onClick={() => onMudar({ cliente: null, prioridades: [], subtipos: [], celulas: [] })}
      >
        <FilterX className="h-4 w-4" aria-hidden="true" />
        Limpar
      </Button>
    </div>
  );
}
